<template>
  <div>
    <b-breadcrumb :items="breadcrumbItems"></b-breadcrumb>

    <h4 qid="maintenance-create-org-title">
      {{$t('maintenance.organizations.create.title')}}
    </h4>

    <div class="card">
      <div class="card-body">
        <div class="form-centered">
          <b-alert v-if="error.length"
                   show
                   variant="danger"
                   qid="create-org-error"
          >
            {{ error }}
          </b-alert>
          <b-form @submit="formSubmit" qid="create-org-form">
            <div class="form-container">
              <b-form-group>
                <label qid="create-org-name-label">
                  {{ $t('maintenance.organizations.create.name_label') }}
                </label>
                <b-form-input
                  qid="create-org-form-name"
                  type="text"
                  v-model="tpForm.name"
                  required
                  :maxLength="255"
                  :placeholder="$t('maintenance.organizations.create.name_placeholder')"
                ></b-form-input>
              </b-form-group>
              <b-form-group>
                <label qid="create-org-description-label">
                  {{ $t('maintenance.organizations.create.description_label') }}
                </label>
                <b-form-textarea
                  qid="create-org-form-description"
                  v-model="tpForm.description"
                  rows="6"
                  max-rows="6"
                  :placeholder="$t('maintenance.organizations.create.description_placeholder')"
                ></b-form-textarea>
              </b-form-group>
              <b-form-group>
                <label qid="create-org-organization-level-label">
                  {{ $t('maintenance.organizations.create.organization_level_label') }}
                </label>
                <multiselect
                    v-model="tpForm.organization_level"
                    :options="levelItems"
                    :placeholder="$t('maintenance.organizations.create.organization_level_placeholder')"
                    label="name"
                    qid="create-org-form-organization-level"
                    @select="selectLevel"
                    @remove="removeLevel"
                ></multiselect>
              </b-form-group>
              <b-form-group ref="select-link-organization" :class="{'d-none':!showLinkOrganizations}">
                <label qid="create-org-link-organization-label">
                  {{ $t('maintenance.organizations.create.link_organization_label') }}
                </label>
                <multiselect
                    v-model="tpForm.link_organization"
                    :options="organizations"
                    track-by="id"
                    :placeholder="$t('maintenance.organizations.create.link_organization_placeholder')"
                    label="name"
                    @search-change="asyncLoadLinkOrganizations"
                    qid="create-org-link-organization-select"
                ></multiselect>
              </b-form-group>
              <b-form-group>
                <label qid="create-s-users-label">
                  {{ $t('maintenance.organizations.create.members_label') }}
                </label>
                <multiselect
                    v-model="selectedUsers"
                    :options="users"
                    :multiple="true"
                    :taggable="false"
                    track-by="slug"
                    :select-label="$t('system.dropdown_select_label')"
                    :selected-label="$t('system.dropdown_selected_label')"
                    :deselect-label="$t('system.dropdown_deselect_label')"
                    :placeholder="$t('maintenance.organizations.create.members_placeholder')"
                    label="name"
                    @search-change="loadUsers"
                    qid="create-s-users-select"
                >
                  <template slot="tag" slot-scope="props">
                    <span class="mr-3 user-tag">
                      <Avatar :object="props.option" size="xs"></Avatar>
                      <span class="ml-2">{{ props.option.name }}</span>
                    </span>
                  </template>
                  <template slot="option" slot-scope="props">
                    <Avatar :object="props.option" size="xs"></Avatar>
                    <span class="ml-2">
                      {{ props.option.name }}
                    </span>
                  </template>
                </multiselect>
              </b-form-group>
            </div>
            <div class="text-right">
              <b-button class="mt-3"
                        variant="secondary"
                        @click="goToList"
                        qid="create-org-cancel-button"
              >
                {{ $t('maintenance.organizations.create.cancel') }}
              </b-button>
              <b-button type="submit"
                        class="mt-3 ml-3"
                        variant="success"
                        :disabled="buttonDisabled"
                        qid="create-org-submit-button"
                        v-if="isGranted(permissions.ORGANIZATION_CREATE)"
              >
                <span v-if="buttonDisabled" class="mr-1">
                  <font-awesome-icon icon="spinner" size="sm" class="fa-spin"/>
                </span>
                {{ $t('maintenance.organizations.create.submit') }}
              </b-button>
            </div>
          </b-form>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
  import piincHttp from '@/util/configureAxios.js'
  import Avatar from '@/components/Avatar.vue';

  export default {
    name: 'CreateOrganization',
    data: function() {
      return {
        breadcrumbItems: [
          {
            text: this.$t('dashboard.title'),
            to: '/'
          },
          {
            text: this.$t('maintenance.title'),
            to: '/maintenance'
          },
          {
            text: this.$t('maintenance.organizations.title'),
            to: '/maintenance/organizations'
          },
          {
            text: this.$t('maintenance.organizations.create.title'),
            active: true
          }
        ],
        tpForm: {
          name: '',
          organization_level: '',
          description: '',
          link_organization: '',
          client:'',
        },
        error: '',
        inputLink: '',
        organizations: [],
        selectedOrganizations: [],
        levelItems: [
          { name: this.$t('system.level') + ' 1', value: '1' },
          { name: this.$t('system.level') + ' 2', value: '2' },
          { name: this.$t('system.level') + ' 3', value: '3' },
          { name: this.$t('system.level') + ' 4', value: '4' },
          { name: this.$t('system.level') + ' 5', value: '5' }
        ],
        showLinkOrganizations: false,
        selectedLevel: '',
        buttonDisabled: false,
        users: [],
        selectedUsers: [],
      }
    },
    computed: {
      getClient() {
        return this.$store.state.client
      }
    },
    watch: {
      // Watch when client changes in store and trigger api reload
      getClient: function () {
        this.loadUsers()
      }
    },
    methods: {
      goToList() {
        this.$router.push('/maintenance/organizations')
      },
      selectLevel(selectedOption) {
        this.selectedLevel = selectedOption
        if (selectedOption && selectedOption.value != 1) {
          this.showLinkOrganizations = true;
        } else {
          this.showLinkOrganizations = false;
        }
        this.asyncLoadLinkOrganizations()
      },
      removeLevel() {
        this.tpForm.link_organization = ''
        this.showLinkOrganizations = false
        this.selectedLevel = ''
      },
      asyncLoadLinkOrganizations(query) {
        let self = this;
        piincHttp.get('organizations', { params:
          {
            smaller_then_organization_level: this.selectedLevel.value,
            keyword: query ? query : undefined,
            client: this.getClient.slug
          }
        }).then(function(response) {
          if (response.data.items.length) {
            self.organizations = response.data.items;
          }
        }, function() {});
      },
      formSubmit(evt) {
        this.buttonDisabled = true;
        let formData = new FormData();

        // Keep the reference in a variable
        // to use it inside function called when promise returned
        let self = this;
        evt.preventDefault();

        formData.append("name", this.tpForm.name);
        formData.append("description", this.tpForm.description);
        formData.append("client", this.getClient.slug);

        // Additional check
        if (this.tpForm.organization_level && this.tpForm.organization_level.value) {
          formData.append("organization_level", this.tpForm.organization_level.value);
        }

        // Additional check
        if (this.tpForm.link_organization && this.tpForm.link_organization.id) {
          formData.append("link_organization", this.tpForm.link_organization.id);
        }

        // Append list of users to FormData
        if (this.selectedUsers && this.selectedUsers.length) {
          for (var i = 0; i < this.selectedUsers.length; i++) {
            formData.append("users[]", this.selectedUsers[i].id);
          }
        }

        // Post FormData object to third parties API
        piincHttp.post('/organizations', formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        }).then(function(response) {
          self.buttonDisabled = false;
          self.$toastr('success', self.$t('maintenance.organizations.create.success'))
          self.goToList();
        }, function(error) {
          if (error.status !== 200) {
            self.buttonDisabled = false;
            self.error = error.data.message;
            if (error.data.errors.organization_level) {
              self.error += ' ' + error.data.errors.organization_level[0]
            }
          }
        })
      },
      loadUsers(query) {
        let self = this;
        piincHttp.get('users', { params:
          {
            client: this.getClient.id,
            keyword: query ? query : undefined,
            per_page: 50
          }
        }).then(function(response) {
          self.users = response.data.items
          if (self.users && !self.users.length && !query) {
            self.users.push({name: self.$t('system.no_results'), $isDisabled: true})
          }
        }, function() {});
      }
    },
    components: {
      Avatar
    },
    created () {
      if (this.getClient && this.getClient.slug) {
        this.loadUsers()
      }
    }
  }
</script>
